import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/translations";
import { resolveImg } from "@/lib/api";
import { Star } from "lucide-react";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 0.9, delay: 0.15 * i, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = ({ content }) => {
  const { lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const words = (content.business_name || "El Rey del Sabor").split(" ");
  void words;

  return (
    <section id="hero" ref={ref} className="relative min-h-screen overflow-hidden flex items-end">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={resolveImg(content.hero_image)} alt="hero" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/30 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 w-full max-w-7xl mx-auto px-5 md:px-8 pb-20 md:pb-28">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="flex items-center gap-3 mb-6"
        >
          <span className="text-xs tracking-[0.25em] uppercase font-bold text-[#FF5A00]">
            {lang === "es" ? content.hero_kicker_es : content.hero_kicker_en}
          </span>
          <span className="flex items-center gap-1 text-xs font-bold text-[#A8E010]">
            <Star size={13} className="fill-[#A8E010]" /> {content.rating}
          </span>
        </motion.div>

        <h1 className="sr-only">{content.business_name} — Food Truck · Stamford, CT · Mexican & Latin Food</h1>
        <motion.div
          initial={{ opacity: 0, scale: 0.82, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="relative w-[80%] max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl"
        >
          <div className="absolute inset-0 blur-3xl bg-[#FF5A00]/20 rounded-full scale-90" aria-hidden />
          <motion.img
            src={resolveImg(content.logo)}
            alt={content.business_name}
            data-testid="hero-logo"
            animate={{ y: [0, -14, 0], rotate: [0, -0.6, 0.6, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="relative w-full h-auto drop-shadow-[0_12px_45px_rgba(0,0,0,0.6)]"
          />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="mt-6 max-w-xl text-lg md:text-xl text-neutral-200 font-medium"
        >
          {lang === "es" ? content.tagline_es : content.tagline_en}
        </motion.p>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-order-btn"
            onClick={() => document.getElementById("visit")?.scrollIntoView({ behavior: "smooth" })}
            className="rounded-full bg-[#FF5A00] hover:bg-[#FF7A33] text-black font-bold uppercase tracking-wide px-8 py-4 transition-all hover:scale-105"
          >
            {t("order", lang)}
          </button>
          <button
            data-testid="hero-menu-btn"
            onClick={() => document.getElementById("menu")?.scrollIntoView({ behavior: "smooth" })}
            className="rounded-full border border-white/25 hover:border-white text-white font-bold uppercase tracking-wide px-8 py-4 transition-colors"
          >
            {t("nav_menu", lang)}
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
};
